import { useEffect, useState } from 'react';
import { Col, Row, Space } from 'antd';
import { HomeFilled, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useSession from 'hooks/useSession';
import useBreadcrumb from 'hooks/useBreadcrumb';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import AssignManifestModal from '../manifests/components/assign-manifest/AssignManifestModal';
import ArchiveManifestModal from '../manifests/components/ArchiveManifestModal';
import ManifestsTable from '../manifests/components/manifests-table/ManifestsTable';
import { Manifest } from 'store/manifests/types';
import useManifests from 'hooks/useManifests';
import Notification from 'components/ui/base/notification/Notification';
import ManifestsFiltersModal from '../manifests/dashboard/components/ManifestsFiltersModal';
import UnarchiveManifestModal from '../manifests/components/UnarchiveManifestModal';
import { hasPermissions } from 'config/roles/hasPermissions';
import ArchivationStatusFilter from '../manifests/dashboard/components/ArchivationStatusFilter';
import useManifestsTable from '../manifests/hooks/useManifestsTable';
import ManifestFilterTags from '../manifests/components/ManifestFilterTags';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import TeamManagerStatsCards from './components/TeamManagerStatsCards';
import { getStatistics } from 'utils/requests/getStatistics';
import { useRequestPromise } from 'hooks/useRequest';
import { getErrorMessage } from 'utils/getErrorMessage';
import useIndividuals from 'hooks/useIndividuals';

// Show manifests assigned to my team (The BE filters them out automatically)
const TeamManagerDashboard = () => {
    const navigate = useNavigate();
    const { listIndividuals } = useIndividuals();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { archiveManifest, unarchiveManifest } = useManifests();
    const { userInfo } = useSession();

    const {
        tags,
        queryInputVal,
        appliedParams,
        isLoadingManifests,
        data,
        archived,
        setQueryInputVal,
        setArchived,
        setPage,
        clearAll,
        clearFilter,
        date,
        filters,
        pagination,
        refetchManifests,
        handleTableChange,
        handleFiltersChange,
        handleDateChange,
    } = useManifestsTable();
    const {
        data: statsData,
        error: statsError,
        isLoading: isLoadingStats,
        refetch: refetchStatsData,
    } = useRequestPromise(() => getStatistics());

    const [selectedManifest, setSelectedManifest] = useState<Manifest | null>(null);
    const [archiving, setArchiving] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showAssignManifestModal, setShowAssignManifestModal] = useState(false);
    const [showArchiveManifestModal, setShowArchiveManifestModal] = useState(false);
    const [showUnarchiveManifestModal, setShowUnarchiveManifestModal] = useState(false);

    const { sort, sortDirection } = appliedParams;

    const canCreateManifests = hasPermissions(userInfo, 'manifests', 'CREATE');

    const handleManifestClick = (record: Manifest) => {
        const manifestId = record.id;
        navigate(`/manifests/${manifestId}`);
    };

    const handleOpenArchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowArchiveManifestModal(true);
    };

    const handleOpenUnarchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowUnarchiveManifestModal(true);
    };

    const handleConfirmArchive = async () => {
        if (!selectedManifest) return;
        try {
            setArchiving(true);
            await archiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Archived',
                description: 'The manifest has been successfully added to the archive',
            });
            refetchManifests();
            refetchStatsData();
            setShowArchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Archivation Error',
                description: errorMessage || 'Could not arhive manifest. Please try again.',
            });
        } finally {
            setArchiving(false);
        }
    };

    const handleConfirmUnarchive = async () => {
        if (!selectedManifest) return;
        try {
            setUnarchiving(true);
            await unarchiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Unarchived',
                description: 'The manifest will now be activated.',
            });
            refetchManifests();
            refetchStatsData();
            setShowUnarchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Unarchivation Error',
                description: errorMessage || 'Could not activate the manifest. Please try again.',
            });
        } finally {
            setUnarchiving(false);
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: '/home',
            },
            {
                breadcrumbName: 'Dashboard',
                path: '/manifests',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        listIndividuals({ size: 200 });
    }, [listIndividuals]);

    return (
        <StyledPageWrapper>
            <Space style={{ width: '100%' }} direction="vertical" size={24}>
                <PageHeader
                    title="My Manifests"
                    icon={<HomeFilled />}
                    sidebar={
                        <Space size={16}>
                            {canCreateManifests ? (
                                <div>
                                    <ArchivationStatusFilter
                                        value={archived ? 'archived' : 'active'}
                                        onChange={(val) => {
                                            if (val === 'active') {
                                                setArchived(false);
                                            } else {
                                                setArchived(true);
                                            }
                                        }}
                                    />
                                    <Button
                                        icon={<PlusOutlined />}
                                        type="primary"
                                        onClick={() => navigate('/manifests/new')}
                                    >
                                        New Manifest
                                    </Button>
                                </div>
                            ) : (
                                <Button type="primary" onClick={() => navigate('/manifests')}>
                                    See All
                                </Button>
                            )}
                        </Space>
                    }
                />

                <TeamManagerStatsCards
                    data={statsData}
                    error={statsError}
                    loading={isLoadingStats}
                    refetch={refetchStatsData}
                />

                <Row justify="space-between">
                    <Col>
                        <Space direction="vertical">
                            <Space size={8}>
                                <SearchBar
                                    inputPlaceholder="Search manifests"
                                    value={queryInputVal}
                                    onSearch={(val) => {
                                        setQueryInputVal(val);
                                        setPage(0);
                                    }}
                                    onClear={() => {
                                        setQueryInputVal('');
                                        setPage(0);
                                    }}
                                />
                                <Button onClick={() => setShowFilterModal(true)}>Filter</Button>
                            </Space>
                        </Space>
                    </Col>
                    <Col>
                        <DatePicker value={date} onChange={handleDateChange} />
                    </Col>
                </Row>

                <Row>
                    <ManifestFilterTags
                        tags={tags}
                        ownerId={appliedParams.ownerId}
                        teamId={appliedParams.teamId}
                        createdBy={appliedParams.createdBy}
                        onClearTag={clearFilter}
                        onClearAll={clearAll}
                    />
                </Row>

                <ManifestsTable
                    manifests={data?.list}
                    loading={isLoadingManifests}
                    onArchiveManifest={handleOpenArchiveModal}
                    onUnarchiveManifest={handleOpenUnarchiveModal}
                    onAssignManifest={(manifest) => {
                        setSelectedManifest(manifest);
                        setShowAssignManifestModal(true);
                    }}
                    onRowClick={handleManifestClick}
                    onChange={handleTableChange}
                    paginator={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                />
            </Space>

            <ManifestsFiltersModal
                visible={showFilterModal}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setShowFilterModal(false)}
                onClearAll={() => clearAll()}
                onClearFilter={clearFilter}
            />

            {selectedManifest && (
                <AssignManifestModal
                    manifest={selectedManifest}
                    open={showAssignManifestModal}
                    onClose={() => setShowAssignManifestModal(false)}
                    onSuccess={() => refetchManifests()}
                />
            )}

            <ArchiveManifestModal
                isArchiving={archiving}
                open={showArchiveManifestModal}
                onConfirm={handleConfirmArchive}
                onClose={() => setShowArchiveManifestModal(false)}
            />
            <UnarchiveManifestModal
                isUnarchiving={unarchiving}
                open={showUnarchiveManifestModal}
                onConfirm={handleConfirmUnarchive}
                onClose={() => setShowUnarchiveManifestModal(false)}
            />
        </StyledPageWrapper>
    );
};

export default TeamManagerDashboard;
